import React, { memo, useEffect, useLayoutEffect } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { usePageSchema } from 'hooks/usePageSchema';

import {
  getSharePreviewImageUrl,
  shouldNavigateToCurrencyYearArchive,
} from 'utils';

import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import HomePageContent from 'components/HomePageContent';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';
import HomePageFooter from 'components/HomePageFooter';

import { Content, Wrapper } from './styles';

const SHARE_DESCRIPTION =
  'Актуальний курс валют в Україні: долар, євро, банки та обмінники — дивись всі курси онлайн';
const SHARE_TITLE = 'Курс валют в Україні на Minfin.com.ua';

const HomePage = memo(({ location, staticContext }) => {
  const { date } = useParams();
  const intl = useIntl();
  const sharePreviewImageUrl = getSharePreviewImageUrl();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    };

    requestAnimationFrame(() => {
      scrollToTop();
      setTimeout(scrollToTop, 0);
    });
  }, [location.pathname]);

  const defaultMetaTitle = intl.formatMessage({
    id: `homePage.metaTitle`,
  });

  const defaultMetaDescription = intl.formatMessage({
    id: `homePage.metaDescription`,
  });

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  if (shouldNavigateToCurrencyYearArchive(date)) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <section>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            meta={[
              {
                property: 'og:title',
                content: SHARE_TITLE,
              },
              {
                property: 'og:description',
                content: SHARE_DESCRIPTION,
              },
              {
                property: 'og:image',
                content: sharePreviewImageUrl,
              },
              {
                name: 'twitter:title',
                content: SHARE_TITLE,
              },
              {
                name: 'twitter:description',
                content: SHARE_DESCRIPTION,
              },
              {
                name: 'twitter:image',
                content: sharePreviewImageUrl,
              },
            ]}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <HomePageContent />
            <PageSidebar isBlogsDisplayed />
            <HomePageFooter />
          </PageInner>
        </LayoutContainer>
      </Content>
    </section>
  );
});

HomePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

HomePage.defaultProps = {
  staticContext: null,
};

export default withRouter(HomePage);
